import { useEffect } from 'react'
import { useNavigate, useParams } from "react-router-dom";
import { createUseStyles } from 'react-jss'
import { titleMarginBottom } from '../constants/layout'
import { formatDateTimeString, formatDateString } from '../utils/dateHelper'
import CardSection from '../components/CardSection'
import DetailsCard from '../components/DetailsCard'
import { TransactionType } from '../redux/types';
import useTransactionActions from '../hooks/useTransactionActions';
import { useAppSelector } from '../redux/hooks';
import FullScreenLoadingIndicator from '../components/FullScreenLoadingIndicator';
import NavLink from '../components/NavLink';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import Link from '../components/Link';
import {formatMoney} from '../utils/moneyHelper';

const TransactionDetails = () => {
  const styles = useStyles()
  const params = useParams<{id: string}>()
  const navigate = useNavigate();

  const { getTransactionDetails } = useTransactionActions()

  const loading = useAppSelector(state => state.transaction.loading)
  const transactionDetails = useAppSelector(state => state.transaction.transactionDetails)

  useEffect(() => {
    if (transactionDetails === null || transactionDetails.id !== params.id) {
      if (params.id) {
        getTransactionDetails(params.id)
      }
    }
  }, [])

  const renderEmployeePayoutDetails = () => {
    if (transactionDetails?.type === TransactionType.EmployeePayoutTransaction) {
      return (
        <DetailsCard>
          <h6>Type specific details</h6>
          <p>Fee: <span>{formatMoney(transactionDetails.employeePayoutTransactionDetails?.fee)}</span></p>
          <p>User: <Link to={`/users/${transactionDetails.employeePayoutTransactionDetails?.userId}`}>{transactionDetails.employeePayoutTransactionDetails?.userGivenName} {transactionDetails.employeePayoutTransactionDetails?.userFamilyName}</Link></p>
          <p>Employer: <Link to={`/employers/${transactionDetails.employeePayoutTransactionDetails?.employerId}`}>{transactionDetails.employeePayoutTransactionDetails?.employerName}</Link></p>
          <p>Payout method: <span>{transactionDetails.employeePayoutTransactionDetails?.payoutMethod.payoutMethodType} {transactionDetails.employeePayoutTransactionDetails?.payoutMethod.target}</span></p>
          <p>Repayment date: <span>{transactionDetails.employeePayoutTransactionDetails && formatDateString(transactionDetails.employeePayoutTransactionDetails.repaymentDate)}</span></p>
            {transactionDetails.state === 'cancelled' &&
              <p>Failure reason: <span>{transactionDetails.employeePayoutTransactionDetails?.failureReason}</span></p>
            }
        </DetailsCard>
      )
    }
  }

  const renderEmployerPayPaymentDetails = () => {
    if (transactionDetails?.type === TransactionType.EmployerPayPaymentTransaction) {
      return (
        <DetailsCard>
          <h6>Type specific details</h6>
          <p>Deducted payouts: <span>{formatMoney(transactionDetails.employerPayPaymentTransactionDetails?.deductedPayoutAmount)}</span></p>
          <p>Deducted fees: <span>{formatMoney(transactionDetails.employerPayPaymentTransactionDetails?.deductedFeeAmount)}</span></p>
          <p>Remaining salary: <span>{formatMoney(transactionDetails.employerPayPaymentTransactionDetails?.remainingPayAmount)}</span></p>
          <p>User: <Link to={`/users/${transactionDetails.employerPayPaymentTransactionDetails?.userId}`}>{transactionDetails.employerPayPaymentTransactionDetails?.userGivenName} {transactionDetails.employerPayPaymentTransactionDetails?.userFamilyName}</Link></p>
          <p>Employer: <Link to={`/employers/${transactionDetails.employerPayPaymentTransactionDetails?.employerId}`}>{transactionDetails.employerPayPaymentTransactionDetails?.employerName}</Link></p>
        </DetailsCard>
      )
    }
  }

  const renderPayPayoutDetails = () => {
    if (transactionDetails?.type === TransactionType.PayPayoutTransaction) {
      return (
        <DetailsCard>
          <h6>Type specific details</h6>
          <p>Payout method: <span>{transactionDetails.payPayoutTransactionDetails?.payoutMethod.payoutMethodType} {transactionDetails.payPayoutTransactionDetails?.payoutMethod.target}</span></p>
          <p>User: <Link to={`/users/${transactionDetails.payPayoutTransactionDetails?.userId}`}>{transactionDetails.payPayoutTransactionDetails?.userGivenName} {transactionDetails.payPayoutTransactionDetails?.userFamilyName}</Link></p>
          <p>Employer: <Link to={`/employers/${transactionDetails.payPayoutTransactionDetails?.employerId}`}>{transactionDetails.payPayoutTransactionDetails?.employerName}</Link></p>
        </DetailsCard>
      )
    }
  }

  const renderTransferDebtsToLandingAccountDetails = () => {
    if (transactionDetails?.type === TransactionType.TransferDebtsToLandingAccountTransaction) {
      return (
        <DetailsCard>
          <h6>Type specific details</h6>
          <p>Employer: <Link to={`/employers/${transactionDetails.transferDebtsToLandingAccountTransactionDetails?.employerId}`}>{transactionDetails.transferDebtsToLandingAccountTransactionDetails?.employerName}</Link></p>
        </DetailsCard>
      )
    }
  }

  const renderTransfePaysToOperatingAccountDetails = () => {
    if (transactionDetails?.type === TransactionType.TransferPaysToOperatingAccountTransaction) {
      return (
        <DetailsCard>
          <h6>Type specific details</h6>
          <p>Employer: <Link to={`/employers/${transactionDetails.transferPaysToOperatingAccountTransactionDetails?.employerId}`}>{transactionDetails.transferPaysToOperatingAccountTransactionDetails?.employerName}</Link></p>
        </DetailsCard>
      )
    }
  }

  const renderEmployerDeductionPaymentDetails = () => {
    if (transactionDetails?.type === TransactionType.EmployerPayDeductionPaymentTransaction) {
      return (
        <DetailsCard>
          <h6>Type specific details</h6>
          <p>Employer: <Link to={`/employers/${transactionDetails.employerPayDeductionPaymentTransactionDetails?.employerId}`}>{transactionDetails.employerPayDeductionPaymentTransactionDetails?.employerName}</Link></p>
        </DetailsCard>
      )
    }
  }

  const renderCreditorPostfundingDetails = () => {
    if (transactionDetails?.type === TransactionType.CreditorPostfundingTransaction) {
      return (
        <DetailsCard>
          <h6>Type specific details</h6>
          <p>Creditor: <span>{transactionDetails.creditorPostfundingTransactionDetails?.creditorName}</span></p>
          <p>Funding cost: <span>{formatMoney(transactionDetails.creditorPostfundingTransactionDetails?.fundingCost)}</span></p>
        </DetailsCard>
      )
    }
  }

  const renderCreditFacilityPayoutDetails = () => {
    const details = transactionDetails?.creditFacilityPayoutTransactionDetails
    if (transactionDetails?.type === TransactionType.CreditFacilityPayoutTransaction && details) {
      return (
        <DetailsCard>
          <h6>Type specific details</h6>
          <p>Payout: <Link to={`/payoutcreditfacilities/${details.payoutCreditFacilityId}/payouts/${details.creditFacilityPayoutId}`}>Details</Link></p>
          <p>Customer reference intent ID: <span>{details.customerReferenceIntentId}</span></p>
          <p>Customer reference payout ID: <span>{details.customerReferencePayoutId}</span></p>
          <p>Payout credit facility: <Link to={`/payoutcreditfacilities/${details.payoutCreditFacilityId}`}>{details.customerName}</Link></p>
          <p>User: <Link to={`/users/${details.userId}`}>{details.userGivenName} {details.userFamilyName}</Link></p>
          <p>Creditor: <span>{details.creditorName} ({details.creditorFundingType})</span></p>
          <p>Payout method: <span>{details.payoutMethod.payoutMethodType} {details.payoutMethod.target}</span></p>
          <p>Repayment date: <span>{formatDateString(details.repaymentDate)}</span></p>
        </DetailsCard>
      )
    }
  }

  const renderCreditFacilityPayoutRepaymentDetails = () => {
    const details = transactionDetails?.creditFacilityPayoutRepaymentTransactionDetails
    if (transactionDetails?.type === TransactionType.CreditFacilityPayoutRepaymentTransaction && details) {
      return (
        <DetailsCard>
          <h6>Type specific details</h6>
          <p>Payout transaction: <Link to={`/transactions/${details.creditFacilityPayoutTransactionId}`}>Details</Link></p>
          <p>Customer reference intent ID: <span>{details.customerReferenceIntentId}</span></p>
          <p>Customer reference payout ID: <span>{details.customerReferencePayoutId}</span></p>
          <p>Payout credit facility: <Link to={`/payoutcreditfacilities/${details.payoutCreditFacilityId}`}>{details.customerName}</Link></p>
          <p>User: <Link to={`/users/${details.userId}`}>{details.userGivenName} {details.userFamilyName}</Link></p>
          <p>Creditor: <span>{details.creditorName} ({details.creditorFundingType})</span></p>
          <p>Repayment bank account ID: <span>{details.repaymentBankAccountId}</span></p>
          <p>Bank account transaction ID: <span>{details.bankAccountTransactionId}</span></p>
        </DetailsCard>
      )
    }
  }

  const renderTransferCreditFacilityPayoutRepaymentsDetails = () => {
    const details = transactionDetails?.transferCreditFacilityPayoutRepaymentsToLandingAccountTransactionDetails
    if (transactionDetails?.type === TransactionType.TransferCreditFacilityPayoutRepaymentsToLandingAccountTransaction && details) {
      return (
        <DetailsCard>
          <h6>Type specific details</h6>
          <p>Payout credit facility: <Link to={`/payoutcreditfacilities/${details.payoutCreditFacilityId}`}>{details.customerName}</Link></p>
          <p>Source bank account ID: <span>{details.sourceBankAccountId}</span></p>
          <p>Bank transfer ID: <span>{details.bankTransferId}</span></p>
          <p>Bank transfer identifier: <span>{details.bankTransferIdentifier}</span></p>
        </DetailsCard>
      )
    }
  }

  return (
    <div className={styles.container}>
      <h1>{transactionDetails && transactionDetails.type.toString().replace('Transaction', '')}</h1>
      <div className={styles.actionBar}>
        <NavLink title='Back' icon={faArrowLeft} onClick={() => navigate(-1)} />
      </div>
      <CardSection>
        <DetailsCard>
          <h6>General</h6>
          <p>Date: <span>{transactionDetails && formatDateTimeString(transactionDetails.date)}</span></p>
          <p>State: <span>{transactionDetails?.state}</span></p>
          <p>Amount: <span>{formatMoney(transactionDetails?.amount)}</span></p>
        </DetailsCard>
        {renderEmployeePayoutDetails()}
        {renderEmployerPayPaymentDetails()}
        {renderPayPayoutDetails()}
        {renderTransferDebtsToLandingAccountDetails()}
        {renderTransfePaysToOperatingAccountDetails()}
        {renderEmployerDeductionPaymentDetails()}
        {renderCreditorPostfundingDetails()}
        {renderCreditFacilityPayoutDetails()}
        {renderCreditFacilityPayoutRepaymentDetails()}
        {renderTransferCreditFacilityPayoutRepaymentsDetails()}
      </CardSection>
      <FullScreenLoadingIndicator visible={loading} />
    </div>
  )
}

export default TransactionDetails

const useStyles = createUseStyles({
  container: {
    '& h1': {
      marginBottom: titleMarginBottom
    }
  },
  actionBar: {
    marginTop: 20,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
})
